// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.
import React, { useMemo } from 'react';
import { Card, Typography } from '@kintent/glide';
import { useCurrentRoute } from 'react-navi';

import * as Styles from './EnterpriseCertificationLeftPanel.styles';
import { getCertificationCardTitle, getCertificationLogo, getCertificationLogoSrcSet } from '../../../../lib/constants';
import { Flex } from '../../../../components/Flex';
import { getSortedCertificationDocuments } from '../lib/getSortedCertificationDocuments';
import { ReactComponent as Ellipse } from '../../../../assets/ellipse-15.svg';

function CertificationDocuments({ certification, documents, isCertificationSelected, subtype }) {
  const urlParams = new URLSearchParams(window.location.search);
  const documentId = urlParams.get('documentId');

  const sortedDocuments = useMemo(() => getSortedCertificationDocuments(documents), [documents]);

  return (
    <Styles.MultipleDocumentsContainer data-testid="enterprise-certification-documents-container">
      {sortedDocuments?.map((document) => {
        const isDocumentSelected = isCertificationSelected && document.id === documentId;
        return (
          <Flex
            alignItems="center"
            gap="8px"
            key={document.id}
            data-testid="enterprise-certification-document"
          >
            <Styles.DocumentLink
              className={isDocumentSelected ? 'active' : ''}
              href={`/certifications/${encodeURIComponent(`${certification.shortName}${subtype || ''}`)}?documentId=${
                document.id
              }`}
            >
              <Ellipse />
              <Typography
                as="p"
                level="9"
              >
                {document.title}
              </Typography>
            </Styles.DocumentLink>
          </Flex>
        );
      })}
    </Styles.MultipleDocumentsContainer>
  );
}

function ListItem({ handleCertificationClick, selectedCertification, item }) {
  const {
    data: { slug },
  } = useCurrentRoute();

  // Catalog related attribtues are nested under the certification key
  const { certification, subtype = '' } = item;

  const isCertificationSelected =
    (slug || `${selectedCertification.certification.shortName}${selectedCertification?.subtype || ''}`) ===
    `${certification.shortName}${subtype}`;

  return (
    <Flex direction="column">
      <Styles.CertificationParentLink
        className={isCertificationSelected ? 'active' : ''}
        data-testid="enterprise-certification-title-logo"
        href={`/certifications/${encodeURIComponent(`${certification.shortName}${item.subtype || ''}`)}`}
        onClick={() => handleCertificationClick(item)}
      >
        <Styles.ComplianceStandardLogo alignItems="center">
          <img
            src={getCertificationLogo(certification.shortName, item.subtype)}
            srcSet={getCertificationLogoSrcSet(certification.shortName, item.subtype)}
            alt="Certification Logo"
          />
        </Styles.ComplianceStandardLogo>
        <Typography
          strength="80%"
          level="8"
        >
          {getCertificationCardTitle(certification.shortName, item.subtype)}
        </Typography>
      </Styles.CertificationParentLink>
      {item.children?.length > 0 && (
        <CertificationDocuments
          certification={certification}
          documents={item.children}
          isCertificationSelected={isCertificationSelected}
          subtype={subtype}
        />
      )}
    </Flex>
  );
}

function EnterpriseCertificationLeftPanel({ onCertificationClick, selectedCertification, teamCertifications }) {
  return (
    <Card
      className="certification-left-panel"
      data-testid="enterprise-certification-left-panel"
    >
      <Flex
        direction="column"
        gap="20px"
      >
        {teamCertifications.map((teamCertification) => (
          <ListItem
            handleCertificationClick={onCertificationClick}
            selectedCertification={selectedCertification}
            key={teamCertification.id}
            item={teamCertification}
          />
        ))}
      </Flex>
    </Card>
  );
}

export default EnterpriseCertificationLeftPanel;
